import api from './api';

export interface Contract {
  id: number;
  reference: string;
  title: string;
  description?: string;
  customer_id: number;
  type: 'maintenance' | 'support' | 'warranty' | 'service';
  status: 'draft' | 'active' | 'expired' | 'terminated' | 'pending';
  start_date: string;
  end_date: string;
  amount: number;
  payment_frequency: 'monthly' | 'quarterly' | 'yearly' | 'one_time';
  terms_and_conditions?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
  customer?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
  };
}

export interface ContractFilters {
  status?: string;
  type?: string;
  customer_id?: number;
  page?: number;
  limit?: number;
}

export interface ContractsResponse {
  success: boolean;
  data: {
    contracts: Contract[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

class ContractsService {
  // Récupérer la liste des contrats
  async getContracts(filters: ContractFilters = {}): Promise<ContractsResponse> {
    const response = await api.get<ContractsResponse>('/contracts', { params: filters });
    return response.data;
  }

  // Récupérer un contrat par son ID
  async getContractById(id: number): Promise<Contract> {
    const response = await api.get<{ success: boolean; data: Contract }>(`/contracts/${id}`);
    return response.data.data;
  }

  // Créer un nouveau contrat
  async createContract(contractData: Partial<Contract>): Promise<Contract> {
    const response = await api.post<{ success: boolean; data: Contract }>('/contracts', contractData);
    return response.data.data;
  }

  // Mettre à jour un contrat
  async updateContract(id: number, contractData: Partial<Contract>): Promise<Contract> {
    const response = await api.put<{ success: boolean; data: Contract }>(`/contracts/${id}`, contractData);
    return response.data.data;
  }

  // Supprimer un contrat
  async deleteContract(id: number): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/contracts/${id}`);
    return response.data;
  }

  // Renouveler un contrat
  async renewContract(id: number, newEndDate: string, newAmount?: number): Promise<Contract> {
    const response = await api.post<{ success: boolean; data: Contract }>(`/contracts/${id}/renew`, { newEndDate, newAmount });
    return response.data.data;
  }

  // Annuler un contrat
  async cancelContract(id: number, reason = 'Annulation via dashboard'): Promise<Contract> {
    const response = await api.post<{ success: boolean; data: Contract }>(`/contracts/${id}/cancel`, { reason });
    return response.data.data;
  }
}

export const contractsService = new ContractsService();
